import { Array as A, Option as O } from '@effect-ts/core'
import { pipe } from '@effect-ts/core/Function'
import _assign from 'lodash/assign'
import _find from 'lodash/find'
import _get from 'lodash/get'
import _map from 'lodash/map'
import type { Module } from 'vuex'
import axios from '../../_framework/axios/trip-dashboard-axios'
import type { Role } from '../../_stores/modules/Users/models/Role'
import type { User } from '../../_stores/modules/Users/models/User'
import { TripDashboardViewModes } from '../TripDashboardPermissionBusConstants'
import { TripDashboardStore, TripDashboardStoreRootState } from './_store_trip_dashboard'
// @ts-ignore
import { setupDashboard } from './TripDashboardsCollection'

// TODO: type
const defaultMockUser = {
  email: 'jane.smith@email.com',
  name: 'Jane Smith',
  id: -1,
  roles: [
    'Client'
  ]
}
// TODO: types...
const userHasRole = (user: User, role: string) =>
  pipe(
    user.roles,
    A.find((r: Role) => r.toString() === role),
    O.isSome
  )

export const UsersStoreModule: Module<
  {
    // TODO:
    currentUser: any
    // TODO:
    clients: any[]
    // TODO:
    contacts: any[]
    impersonatingUserId: O.Option<number> // <-1 is default mock user
    currentViewMode: O.Option<number> // TODO: clean this type
  },
  TripDashboardStoreRootState
> = {
  state: {
    currentUser: _get(window, 'appInitialData.stores.User', null),
    contacts: _get(window, 'appInitialData.stores.Contacts', []),
    impersonatingUserId: O.none,
    clients: [],
    currentViewMode: O.none
  },
  getters: {
    currentRealUser: state => state.currentUser,
    currentRealUserIsOperator: state =>
      state.currentUser
      && (userHasRole(state.currentUser, 'SuperAdmin')
        || userHasRole(state.currentUser, 'Operator')),
    currentRealUserIsSuperAdmin: state => userHasRole(state.currentUser, 'SuperAdmin'),
    currentUser: state =>
      pipe(
        state.impersonatingUserId,
        O.fold(
          () => state.currentUser as number,
          (impersonatingUserId: number) =>
            pipe(
              state.clients as { id: number }[], // TODO: review this
              A.find((
                // TODO: review this
                clientUser: { id: number }
              ) => clientUser.id === impersonatingUserId),
              O.getOrElse(() => defaultMockUser)
            )
        )
      ),
    User: state => state.currentUser,
    Clients: state => state.clients,
    Contacts: state => state.contacts,
    TripUsers: (state, getters) => {
      return getters.currentTripDashboardId ?
        _map(getters.Trips[getters.currentTripDashboardId].users, tripUser => {
          const client = _find(state.clients, clientItem => {
            return tripUser.id === clientItem.id
          })
          if (client) {
            _assign(tripUser, client)
          }
          return tripUser
        }) :
        []
    },
    isOperator: (_, getters) => getters.currentRealUserIsOperator,
    canAccessSection: (state, getters) => {
      return (sectionName: string) => {
        if (!getters.Dashboard[`dashboard-section-enabled-${sectionName}`]) {
          return false
        } else if (getters.isOperator) {
          return true
        } // @todo this is imperfect (why?!)
        else if (getters.currentTripDashboardId && getters.User) {
          // console.log(state.clients);
          const client = _find(getters.Trips[getters.currentTripDashboardId].users, clientItem => {
            // console.log(getters.User, client);
            return getters.User.id === clientItem.id
          })
          if (client) {
            return !(client.permissions && client.permissions[sectionName] === false)
          }
        } else if (getters.User) {
          return !(getters.User.permissions && getters.User.permissions[sectionName] === false)
        } else {
          return false
        }
      }
    },
    getTripUser: (_state, getters) => {
      return (userId: number) => {
        return _find(getters.TripUsers, client => {
          return client.id === userId
        })
      }
    },
    currentUserHasPermissionTo: (_state, getters) =>
      (permission: string) =>
        (getters.User.permissions ?? [])
          .filter((r: { name: string; value: string }) =>
            r.name === permission || r.value === permission
          )
          .length
  },
  mutations: {
    syncClientExtras(state, user) {
      if (!Array.isArray(state.clients)) {
        state.clients = []
        return
      }

      const clientIndex = state.clients.findIndex(client => client.id === user.id)

      if (clientIndex !== -1) {
        state.clients[clientIndex].extras = user.extras
      }
    },
    setClientUsers(state, payload) {
      state.clients = payload
    },
    impersonateUser(state, userIdOpt) {
      state.impersonatingUserId = userIdOpt
      state.currentViewMode = O.some(
        O.isSome(userIdOpt) ?
          TripDashboardViewModes.CLIENT_VIEW :
          TripDashboardViewModes.OPERATOR_VIEW
      )
    },
    setContactsState(state, payload) {
      state.contacts = payload
    }
  },
  actions: {
    updateUserExtras(context, payload) {
      axios
        .post(`/clients/${payload.user.id}/updateExtras`, {
          extras: payload.user.extras
        })
        .then(response => {
          if (response.status === 200) {
            context.commit('syncClientExtras', payload.user)
          }
        })
    },
    createUser(context, payload) {
      axios.post(`${context.getters.currentTripDashboardId}/createUser`, payload).then(response => {
        if (response.status === 200) {
          context.commit('updateTripUsers', response.data.users)

          const trip = context.getters.tripDashboards[context.getters.currentTripDashboardId]
          trip.sign_requests = response.data.sign_requests
          setupDashboard(context, context.getters.currentTripDashboardId, trip)

          response.data.users.forEach((user: any) => {
            context.commit('addClientIfNotExists', user)
          })
        }
      })
    },

    addUser(context, payload) {
      if (this.getters.isOperator) {
        axios.post(`${context.getters.currentTripDashboardId}/addUser`, payload).then(response => {
          if (response.status === 200) {
            context.commit('updateTripUsers', response.data.users)

            const trip = context.getters.tripDashboards[context.getters.currentTripDashboardId]
            trip.sign_requests = response.data.sign_requests
            setupDashboard(context, context.getters.currentTripDashboardId, trip)

            response.data.users.forEach((user: any) => {
              context.commit('addClientIfNotExists', user)
            })
          }
        })
      }
    },

    unlinkUser(context, payload) {
      if (this.getters.isOperator) {
        axios.post(`${context.getters.currentTripDashboardId}/unlinkUser`, payload).then(
          response => {
            if (response.status === 200) {
              context.commit('updateTripUsers', response.data.users)

              const trip = context.getters.tripDashboards[context.getters.currentTripDashboardId]
              trip.sign_requests = response.data.sign_requests
              setupDashboard(context, context.getters.currentTripDashboardId, trip)

              context.commit(
                'removeClientsNotInList',
                response.data.users.map((user: any) => user.id)
              )
            }
          }
        )
      }
    },

    setUserPermission(context, payload) {
      if (this.getters.isOperator) {
        axios
          .post(`${context.getters.currentTripDashboardId}/setUserPermission`, payload)
          .then(response => {
            if (response.status === 200) {
              context.commit('updateTripUsers', response.data.users)
              TripDashboardStore.commit('setClientUsers', response.data.users)
            }
          })
      }
    },
    getContacts(context, payload) {
      if (this.getters.isOperator && payload && payload.length > 2) {
        return axios
          .post(`${context.getters.currentTripDashboardId}/getContacts`, { search_key: payload })
          .then(response => {
            if (response.status === 200) {
              context.commit('setContactsState', response.data)
            }
          })
      } else {
        context.commit('setContactsState', [])
      }
    }
  }
}
